import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { addDays, format, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, startOfDay, isSameDay, getDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import { api, gbp2 } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function Booking() {
  const { cart, subtotal, depositTotal, remaining, totalDuration, clearCart, refreshCart } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [client, setClient] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", postcode: "", dob: "", emergency_contact: "",
    notes: "", heard_about: "", marketing_consent: false, medical_consent: false, age_confirmation: false,
  });
  const [referralCode, setReferralCode] = useState("");
  const [referral, setReferral] = useState(null); // {valid, credit, reason}
  const [storedCredit, setStoredCredit] = useState(0);
  const [consultationPreview, setConsultationPreview] = useState({ credits: [] });
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Course SKUs — a cart containing any of these cannot combine with
  // referral / stored credit / consultation credit / loyalty rewards.
  // The backend already strips those at /bookings; this preview just
  // stops the checkout line items from misleading the client.
  const COURSE_SLUGS = new Set([
    "microneedling-course-of-3",
    "microneedling-course-of-6",
    "profhilo-course-of-2",
    "polynucleotides-course-of-3",
  ]);
  const hasCourseInCart = (cart || []).some((i) => COURSE_SLUGS.has(i.slug));
  // CLAÈ Loyalty auto-detect — fetched on email blur at step 2. If the
  // client has an unredeemed reward attached to their Client ID, we
  // surface a toggle at checkout so they can confirm applying it. Never
  // silent — matches user choice 4a.
  const [loyaltyReward, setLoyaltyReward] = useState(null);       // {id, kind, label, discount_pct}
  const [applyLoyalty, setApplyLoyalty] = useState(true);
  // Promo code — admin-managed, one-time-use, percentage off final total.
  // Deposit is intentionally NOT discounted; the saving comes off the
  // in-clinic balance only (spec §3). Server re-validates on POST.
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);  // {code, discount_pct}
  const [promoError, setPromoError] = useState("");
  // Hard consent requirement — one signature per unique template needed
  // by the cart's treatments. Pay button is blocked until every entry has
  // a signature. Server re-validates on POST /bookings.
  const [requiredConsents, setRequiredConsents] = useState([]);  // [{template_key,label,declarations,treatment_name}]
  const [consentSignatures, setConsentSignatures] = useState({}); // { [template_key]: "Signed name" }
  const [openConsentKey, setOpenConsentKey] = useState(null);
  useEffect(() => {
    if (!cart.length) { setRequiredConsents([]); return; }
    const ids = cart.map((i) => i.treatment_id).join(",");
    api.get("/bookings/consent-requirements", { params: { treatment_ids: ids } })
      .then((r) => setRequiredConsents(r.data.required || []))
      .catch(() => setRequiredConsents([]));
  }, [cart]);
  const allConsentsSigned = useMemo(() =>
    requiredConsents.every((c) => (consentSignatures[c.template_key] || "").trim().length > 1),
    [requiredConsents, consentSignatures]);

  useEffect(() => {
    api.get("/settings").then((r) => setCancellationPolicy(r.data.cancellation_policy || "")).catch(() => {});
    // Re-sync cart lines with the latest server-side treatment records so any
    // admin-side changes to duration / price / deposit are picked up before we
    // request availability slots.
    refreshCart().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (cart.length === 0 && !submitting) nav("/"); }, [cart, nav, submitting]);
  const referralGift = (referral?.valid && !hasCourseInCart) ? referral.credit : 0;
  const usableStoredCredit = hasCourseInCart ? 0 : Math.max(0, Math.min(storedCredit, subtotal - referralGift));
  const hasConsultation = cart.some((i) => (i.category || "").toLowerCase() === "consultation" || i.consultation_target_label);
  // Only count credits that (a) are Active + attendance_confirmed and (b) match
  // at least one non-consultation cart item's target_key. Backend re-validates.
  const consultationCreditsToApply = React.useMemo(() => {
    if (hasConsultation || hasCourseInCart) return 0;
    const activeTargets = new Set((consultationPreview.credits || []).map((c) => c.target_treatment_key));
    if (activeTargets.size === 0) return 0;
    return (consultationPreview.credits || []).reduce((n, c) => n + Number(c.amount || 0), 0);
  }, [consultationPreview, hasConsultation, hasCourseInCart]);
  const totalCredit = referralGift + usableStoredCredit + consultationCreditsToApply;
  const preLoyaltyTotal = Math.max(0, subtotal - totalCredit);
  // Loyalty discount is applied to the single highest-priced eligible line
  // (matches the backend's authoritative application). Consultation-only
  // baskets and course purchases can never redeem a reward.
  const loyaltyDiscount = useMemo(() => {
    if (!loyaltyReward || !applyLoyalty || hasConsultation || hasCourseInCart) return 0;
    const eligible = cart.filter((i) => !i.is_topup_line && !i.is_model_line);
    if (eligible.length === 0) return 0;
    const top = [...eligible].sort((a, b) => Number(b.price || 0) - Number(a.price || 0))[0];
    return Math.round(Number(top.price || 0) * Number(loyaltyReward.discount_pct || 0) * 100) / 100;
  }, [loyaltyReward, applyLoyalty, hasConsultation, hasCourseInCart, cart]);
  const finalTotal = Math.max(0, preLoyaltyTotal - loyaltyDiscount);
  // Promo discount — percentage of the current (post-everything-else)
  // total. Server re-computes authoritatively; this is the UI preview.
  const promoDiscount = useMemo(() => {
    if (!promoApplied || hasConsultation) return 0;
    return Math.round(finalTotal * Number(promoApplied.discount_pct || 0)) / 100;
  }, [promoApplied, finalTotal, hasConsultation]);
  const finalTotalAfterPromo = Math.max(0, finalTotal - promoDiscount);
  // Consultation: full-price payment (not a deposit); other treatments: deposit only.
  // Deposit is calculated against the pre-promo total — spec §3: promo does NOT reduce deposit.
  const rawDeposit = hasConsultation ? finalTotalAfterPromo : depositTotal;
  const finalDeposit = Math.max(0, Math.min(rawDeposit, finalTotalAfterPromo));
  const finalRemaining = Math.max(0, finalTotalAfterPromo - finalDeposit);

  const days = useMemo(() => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    return eachDayOfInterval({ start, end });
  }, [monthDate]);

  const loadSlots = async (d) => {
    setLoadingSlots(true);
    try {
      const r = await api.get("/slots", { params: { date: format(d, "yyyy-MM-dd"), duration: totalDuration } });
      setSlots(r.data.slots);
      // If the currently-selected time is no longer in the returned list
      // (it just ticked into the past, or another client just took it),
      // clear it so the client picks a fresh one.
      setSelectedTime((prev) => (prev && !r.data.slots.includes(prev) ? null : prev));
    } catch { toast.error("Could not load times"); }
    setLoadingSlots(false);
  };

  // Auto-refresh today's slots every 30 seconds so past times drop off
  // automatically without the client needing to reload the page. Only
  // runs while step 1 is open AND the selected date is today (London).
  useEffect(() => {
    if (step !== 1 || !selectedDate) return;
    const londonToday = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" }));
    const isTodayLondon = format(selectedDate, "yyyy-MM-dd") === format(londonToday, "yyyy-MM-dd");
    if (!isTodayLondon) return;
    const id = setInterval(() => { loadSlots(selectedDate); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedDate, totalDuration]);

  const validateReferral = async () => {
    const code = referralCode.trim();
    if (!code) { setReferral(null); return; }
    if (!client.email) { toast.error("Enter your email first"); return; }
    try {
      const r = await api.post("/referrals/validate", { code, email: client.email });
      setReferral(r.data);
      if (r.data.valid) toast.success(`£${r.data.credit} credit applied from ${r.data.referrer_first_name}`);
      else toast.error(r.data.reason || "Code invalid");
    } catch { toast.error("Could not check code"); }
  };

  const previewCredit = async (email) => {
    if (!email || !email.includes("@")) { setStoredCredit(0); setConsultationPreview({ credits: [] }); setLoyaltyReward(null); return; }
    try {
      const r = await api.get("/credit/preview", { params: { email } });
      const bal = Number(r.data.credit_balance || 0);
      setStoredCredit(bal);
      if (bal > 0) toast.success(`£${bal.toFixed(2)} credit will be applied automatically`);
    } catch { setStoredCredit(0); }
    try {
      const r2 = await api.get("/consultation-credit/preview", { params: { email } });
      setConsultationPreview(r2.data || { credits: [] });
      const nActive = (r2.data?.credits || []).length;
      if (nActive > 0) {
        toast.success(`£${(nActive * 50).toFixed(0)} consultation credit available — will auto-apply if this booking matches`);
      }
    } catch { setConsultationPreview({ credits: [] }); }
    // Loyalty auto-detect — check whether this client has an unredeemed reward
    try {
      const r3 = await api.post("/loyalty/lookup", { email, phone: client.phone, dob: client.dob });
      if (r3.data?.has_reward) {
        setLoyaltyReward(r3.data.reward);
        setApplyLoyalty(true);
        toast.success(`CLAÈ Loyalty reward available — ${r3.data.reward.label}`);
      } else {
        setLoyaltyReward(null);
      }
    } catch { setLoyaltyReward(null); }
  };

  const submit = async () => {
    if (!client.medical_consent) { toast.error("Please confirm medical consent"); return; }
    if (!client.age_confirmation) { toast.error("You must confirm you are 18 or older"); return; }
    if (!termsAccepted) { toast.error("Please accept the cancellation policy"); return; }
    setSubmitting(true);
    // Final real-time availability check before we call POST /bookings so
    // a slot that just passed (or was taken by someone else) never gets
    // through. The server also re-validates — this is a UX shortcut.
    try {
      const r = await api.get("/slots", { params: { date: format(selectedDate, "yyyy-MM-dd"), duration: totalDuration } });
      if (!r.data.slots.includes(selectedTime)) {
        setSlots(r.data.slots);
        setSelectedTime(null);
        toast.error("This appointment time is no longer available. Please select another available time.");
        setStep(1);
        setSubmitting(false);
        return;
      }
    } catch { /* fall through to server-side validation */ }
    try {
      const body = {
        items: cart.map((i) => ({
          treatment_id: i.treatment_id,
          quantity: i.quantity,
          consultation_target_key: i.consultation_target_key,
          consultation_target_label: i.consultation_target_label,
        })),
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        client,
        origin_url: window.location.origin,
        consents: [],
        // pre_consent_signatures intentionally omitted — the inline
        // consent block was removed from Step 3, and the backend no
        // longer requires signed_keys on POST /bookings. Consent is
        // collected via the post-booking email flow.
        referral_code: referral?.valid ? referralCode.trim().toUpperCase() : null,
        loyalty_reward_id: (loyaltyReward && applyLoyalty && !hasConsultation) ? loyaltyReward.id : null,
        promo_code: (promoApplied && !hasConsultation) ? promoApplied.code : null,
      };
      const r = await api.post("/bookings", body);
      // Navigate FIRST, then clear cart. Clearing cart before nav races with the
      // "cart empty → back to catalogue" guard below and bounces the client to /.
      if (r.data.checkout_url) {
        clearCart();
        window.location.href = r.data.checkout_url;
      } else {
        nav(`/booking/success?reference=${r.data.reference}`, { replace: true });
        // Defer clearCart until after the route change has committed.
        setTimeout(() => clearCart(), 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <p className="mono-label mb-3">Booking · Step {step} of 3</p>
        <h1 className="font-serif text-5xl font-light text-stone-900">Book your appointment</h1>

        <div className="mt-8 flex gap-2 mono-label">
          {["Date & Time", "Your Details", "Review & Pay"].map((label, i) => (
            <div key={label} className={`flex-1 border-b-2 pb-2 ${step > i ? "border-stone-900 text-stone-900" : "border-stone-300 text-stone-400"}`}>
              {i + 1}. {label}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="flex items-center justify-between mb-4">
                <button data-testid="prev-month" onClick={() => setMonthDate(addDays(startOfMonth(monthDate), -1))} className="p-2 hover:bg-alt"><ChevronLeft size={18} /></button>
                <p className="font-serif text-2xl">{format(monthDate, "MMMM yyyy")}</p>
                <button data-testid="next-month" onClick={() => setMonthDate(addDays(endOfMonth(monthDate), 1))} className="p-2 hover:bg-alt"><ChevronRight size={18} /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 mono-label text-center mb-2">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d}>{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: (getDay(startOfMonth(monthDate)) + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
                {days.map((d) => {
                  const past = isBefore(d, startOfDay(new Date()));
                  const active = selectedDate && isSameDay(d, selectedDate);
                  return (
                    <button
                      key={d.toISOString()}
                      disabled={past}
                      data-testid={`day-${format(d, "yyyy-MM-dd")}`}
                      onClick={() => { setSelectedDate(d); loadSlots(d); }}
                      className={`aspect-square text-sm border ${active ? "bg-stone-900 text-white border-stone-900" : past ? "text-stone-300 border-transparent" : "hairline hover:border-stone-900 hover:bg-alt"}`}
                    >
                      {format(d, "d")}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="mono-label mb-3">Available Times</p>
              {!selectedDate && <p className="text-stone-500 text-sm">Select a date to view times.</p>}
              {selectedDate && loadingSlots && <p className="text-stone-500 text-sm">Loading…</p>}
              {selectedDate && !loadingSlots && slots.length === 0 && <p className="text-stone-500 text-sm">No availability on this date.</p>}
              <div className="grid grid-cols-3 gap-2">
                {slots.map((s) => (
                  <button
                    key={s}
                    data-testid={`slot-${s}`}
                    onClick={() => setSelectedTime(s)}
                    className={`py-3 text-sm border ${selectedTime === s ? "bg-stone-900 text-white border-stone-900" : "hairline hover:border-stone-900"}`}
                  >{s}</button>
                ))}
              </div>
              <button
                data-testid="step1-next"
                disabled={!selectedDate || !selectedTime}
                onClick={() => setStep(2)}
                className="mt-8 w-full bg-stone-900 text-white py-4 mono-label disabled:opacity-30 hover:bg-[#C5A059] transition"
                style={{ color: "#fff" }}
              >Continue</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="first_name" label="First Name" val={client} set={setClient} />
            <Field id="last_name" label="Last Name" val={client} set={setClient} />
            <Field id="email" label="Email" type="email" val={client} set={setClient} />
            <Field id="phone" label="Mobile Phone *" val={client} set={setClient} />
            <Field id="address" label="Full Address" val={client} set={setClient} full />
            <Field id="postcode" label="Postcode" val={client} set={setClient} />
                <div>
              <div className="flex items-end justify-between gap-3">
                <label className="mono-label" htmlFor="input-dob">Date of Birth *</label>
                {client.dob && (
                  <button
                    type="button"
                    data-testid="clear-dob"
                    onClick={() => setClient((c) => ({ ...c, dob: "" }))}
                    className="mono-label text-[10px] text-stone-500 hover:text-stone-900 underline underline-offset-2"
                  >
                    Clear
                  </button>
                )}
              </div>
              <input
                id="input-dob"
                data-testid="input-dob"
                type="date"
                value={client.dob || ""}
                min="1920-01-01"
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                onChange={(e) => {
                  // Functional updater so the handler can never re-assert a
                  // stale state value — fixes the mobile "Clear doesn't
                  // actually clear" bug.
                  const v = e.target.value;
                  setClient((c) => ({ ...c, dob: v }));
                }}
                onBlur={(e) => {
                  // Only run the 18+ check when a date has actually been
                  // committed. An empty value (via the native Clear button
                  // or our explicit Clear link) must never trigger the
                  // warning or mutate state.
                  const v = e.target.value;
                  if (!v) return;
                  const dob = new Date(v);
                  const today = new Date();
                  const eighteenth = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
                  if (eighteenth > today) {
                    toast.error("You must be 18 or older to book");
                    setClient((c) => ({ ...c, dob: "" }));
                  }
                }}
                className="mt-2 w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900"
              />
              <p className="mt-1 text-[11px] text-stone-500">You must be 18 or older to book.</p>
            </div>
            <Field id="emergency_contact" label="Emergency Contact (optional)" val={client} set={setClient} />
            <Field id="heard_about" label="How did you hear about CLAÈ?" val={client} set={setClient} full />
            <div className="sm:col-span-2">
              <label className="mono-label">Notes (optional)</label>
              <textarea data-testid="input-notes" value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })}
                className="mt-2 w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900" rows={3} />
            </div>
            <label className="sm:col-span-2 flex items-start gap-3 text-sm text-stone-700 mt-2">
              <input data-testid="input-medical-consent" type="checkbox" checked={client.medical_consent} onChange={(e) => setClient({ ...client, medical_consent: e.target.checked })} className="mt-1" />
              <span>I confirm I have read the treatment aftercare information and I am fit for treatment (no pregnancy, breastfeeding, active infection or contraindication).</span>
            </label>
            <label className="sm:col-span-2 flex items-start gap-3 text-sm text-stone-700">
              <input data-testid="input-age-confirmation" type="checkbox" checked={client.age_confirmation} onChange={(e) => setClient({ ...client, age_confirmation: e.target.checked })} className="mt-1" />
              <span>I confirm I am 18 years of age or older.</span>
            </label>
            <label className="sm:col-span-2 flex items-start gap-3 text-sm text-stone-700">
              <input data-testid="input-marketing-consent" type="checkbox" checked={client.marketing_consent} onChange={(e) => setClient({ ...client, marketing_consent: e.target.checked })} className="mt-1" />
              <span>I would like to receive occasional emails about new CLAÈ treatments and offers.</span>
            </label>
            <div className="sm:col-span-2 flex justify-between mt-6">
              <button data-testid="step2-back" onClick={() => setStep(1)} className="mono-label text-stone-600 hover:text-stone-900">← Back</button>
              <button
                data-testid="step2-next"
                disabled={!client.first_name || !client.last_name || !client.email || !client.phone || !client.dob || !client.address || !client.postcode || !client.medical_consent || !client.age_confirmation}
                onClick={() => { previewCredit(client.email); setStep(3); }}
                className="bg-stone-900 text-white px-8 py-4 mono-label disabled:opacity-30 hover:bg-[#C5A059] transition"
                style={{ color: "#fff" }}
              >Continue</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="border hairline bg-white p-6 space-y-3">
                <p className="mono-label">Appointment</p>
                <p className="font-serif text-2xl">{selectedDate && format(selectedDate, "EEEE, d MMMM yyyy")} · {selectedTime}</p>
                <p className="text-sm text-stone-600">Duration: {totalDuration} minutes</p>
                <div className="pt-4 border-t hairline">
                  <p className="mono-label mb-2">Your Treatments</p>
                  {cart.map((i) => (
                    <div key={i.treatment_id} className="flex justify-between py-2 text-sm">
                      <span>{i.name} × {i.quantity}</span><span>{gbp2(i.price * i.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t hairline">
                  <p className="mono-label mb-2">Client</p>
                  <p className="text-sm">{client.first_name} {client.last_name} · {client.email} · {client.phone}</p>
                  <p className="text-sm text-stone-600">{client.address}, {client.postcode}</p>
                </div>
                {cancellationPolicy && (
                  <div className="pt-4 border-t hairline" data-testid="cancellation-policy-block">
                    <p className="mono-label mb-2">Cancellation Policy</p>
                    <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line">
                      {hasConsultation
                        ? cancellationPolicy
                            .replace(/Deposits/g, "Consultation costs")
                            .replace(/deposits/g, "consultation costs")
                            .replace(/Deposit/g, "Consultation cost")
                            .replace(/deposit/g, "consultation cost")
                        : cancellationPolicy}
                    </p>
                    <label className="mt-4 flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                      <input data-testid="terms-accepted" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-1" />
                      <span>I have read and agree to the CLAÈ 48-hour cancellation and late arrival policy above.</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <aside className="border hairline bg-white p-6 h-fit space-y-3">
              <div>
                <p className="mono-label mb-2">Referral Code</p>
                <div className="flex gap-2">
                  <input
                    data-testid="referral-input"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferral(null); }}
                    placeholder="FRIEND-1234"
                    className="flex-1 min-w-0 border hairline p-2 text-sm font-mono focus:outline-none focus:border-stone-900"
                  />
                  <button data-testid="referral-apply" onClick={validateReferral} className="mono-label border border-stone-900 px-3 flex-shrink-0 whitespace-nowrap hover:bg-stone-900 hover:text-white transition">Apply</button>
                </div>
                {referral?.valid && <p className="mono-label gold mt-2" data-testid="referral-applied">− £{referral.credit} credit applied</p>}
              </div>
              {!hasConsultation && (
                <div className="pt-3 border-t hairline">
                  <p className="mono-label mb-2">Promo Code</p>
                  <div className="flex gap-2">
                    <input
                      data-testid="promo-input"
                      value={promoCodeInput}
                      onChange={(e) => { setPromoCodeInput(e.target.value.toUpperCase()); setPromoError(""); }}
                      placeholder="Enter promo code"
                      disabled={!!promoApplied}
                      className="flex-1 min-w-0 border hairline p-2 text-sm font-mono focus:outline-none focus:border-stone-900 disabled:bg-stone-50"
                    />
                    {promoApplied ? (
                      <button
                        data-testid="promo-remove"
                        onClick={() => { setPromoApplied(null); setPromoCodeInput(""); setPromoError(""); }}
                        className="mono-label border border-stone-900 px-3 flex-shrink-0 whitespace-nowrap hover:bg-stone-900 hover:text-white transition"
                      >Remove</button>
                    ) : (
                      <button
                        data-testid="promo-apply"
                        onClick={async () => {
                          const code = promoCodeInput.trim().toUpperCase();
                          if (!code) { setPromoError("Please enter a promo code."); return; }
                          try {
                            const r = await api.post("/promo-codes/validate", { code });
                            if (r.data?.valid) {
                              setPromoApplied({ code: r.data.code, discount_pct: r.data.discount_pct });
                              setPromoError("");
                              toast.success(`Promo code applied — ${r.data.discount_pct}% off`);
                            } else {
                              setPromoApplied(null);
                              setPromoError(r.data?.reason || "This promo code is not valid.");
                            }
                          } catch {
                            setPromoApplied(null);
                            setPromoError("Could not validate promo code — please try again.");
                          }
                        }}
                        className="mono-label border border-stone-900 px-3 flex-shrink-0 whitespace-nowrap hover:bg-stone-900 hover:text-white transition"
                      >Apply</button>
                    )}
                  </div>
                  {promoApplied && (
                    <p className="mono-label gold mt-2" data-testid="promo-applied">
                      {promoApplied.code} — {promoApplied.discount_pct}% off applied
                    </p>
                  )}
                  {promoError && <p className="text-xs text-red-600 mt-2" data-testid="promo-error">{promoError}</p>}
                </div>
              )}
              <div className="pt-3 border-t hairline space-y-1">
                <div className="flex justify-between text-sm text-stone-600"><span className="mono-label">Subtotal</span><span>{gbp2(subtotal)}</span></div>
                {consultationCreditsToApply > 0 && (
                  <div className="flex justify-between text-sm gold" data-testid="consultation-credit-line">
                    <span className="mono-label gold">Consultation Credit</span>
                    <span>− {gbp2(consultationCreditsToApply)}</span>
                  </div>
                )}
                {usableStoredCredit > 0 && (
                  <div className="flex justify-between text-sm gold" data-testid="stored-credit-line">
                    <span className="mono-label gold">Your CLAÈ credit</span>
                    <span>− {gbp2(usableStoredCredit)}</span>
                  </div>
                )}
                {referralGift > 0 && <div className="flex justify-between text-sm gold"><span className="mono-label gold">Referral</span><span>− {gbp2(referralGift)}</span></div>}
                {loyaltyReward && !hasConsultation && (
                  <div className="pt-2 border-t hairline" data-testid="loyalty-reward-block">
                    <label className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                      <input
                        data-testid="apply-loyalty"
                        type="checkbox"
                        checked={applyLoyalty}
                        onChange={(e) => setApplyLoyalty(e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="mono-label gold block mb-0.5">CLAÈ LOYALTY REWARD</span>
                        <span className="text-xs text-stone-600">Apply {loyaltyReward.label} to this booking.</span>
                      </span>
                    </label>
                    {applyLoyalty && loyaltyDiscount > 0 && (
                      <div className="flex justify-between text-sm gold mt-2" data-testid="loyalty-discount-line">
                        <span className="mono-label gold">Loyalty reward</span>
                        <span>− {gbp2(loyaltyDiscount)}</span>
                      </div>
                    )}
                  </div>
                )}
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-sm gold" data-testid="promo-discount-line">
                    <span className="mono-label gold">Promo ({promoApplied.discount_pct}%)</span>
                    <span>− {gbp2(promoDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t hairline">
                  <span className="mono-label">Total</span><span className="font-serif text-2xl" data-testid="final-total">{gbp2(finalTotalAfterPromo)}</span>
                </div>
              </div>
              <div className="pt-3 border-t hairline">
                <p className="mono-label">{hasConsultation ? "Amount to pay" : "Pay today"}</p>
                <p className="font-serif text-2xl gold" data-testid="final-deposit">{gbp2(finalDeposit)}</p>
                {hasConsultation ? (
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Your £50 consultation fee secures your dedicated appointment time and is redeemable against your selected treatment following your consultation.
                  </p>
                ) : (
                  <>
                    <p className="mono-label mt-3">Balance in clinic</p>
                    <p className="font-serif text-lg">{gbp2(finalRemaining)}</p>
                  </>
                )}
              </div>

              {/* Inline required-consent block removed — consent is now
                  captured entirely via the post-booking email flow
                  (`/consent/{bid}`). The backend no longer enforces
                  signed_keys on POST /bookings, so submission works
                  without any inline signatures. */}
              <button
                data-testid="pay-deposit"
                disabled={submitting || !termsAccepted}
                onClick={submit}
                className="mt-4 w-full bg-stone-900 text-white py-4 mono-label disabled:opacity-30 hover:bg-[#C5A059] transition"
                style={{ color: "#fff" }}
              >
                {submitting ? "Processing…" :
                 hasConsultation ? `Pay ${gbp2(finalDeposit)} Consultation Fee` :
                 finalDeposit > 0 ? `Pay ${gbp2(finalDeposit)} Deposit` : "Confirm Booking"}
              </button>
              <p className="text-xs text-stone-500 text-center mt-2">{hasConsultation ? "No consent form required for consultation." : "A consent form will be sent to your email after booking. Please complete it before your appointment."}</p>
              <button data-testid="step3-back" onClick={() => setStep(2)} className="w-full mono-label text-stone-600 hover:text-stone-900">← Back</button>
            </aside>
          </div>
        )}
      </main>
    </>
  );
}

const Field = ({ id, label, type = "text", val, set, full, max }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mono-label">{label}</label>
    <input
      data-testid={`input-${id}`}
      type={type}
      value={val[id] || ""}
      onChange={(e) => set({ ...val, [id]: e.target.value })}
      max={max}
      className="mt-2 w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900"
    />
  </div>
);
